import { ref, computed, watch } from 'vue';

export function useKanbanModals(onTicketCreatedCallback, columnTicketsRef, router = null, route = null, store = null) {
  // Ticket View Modal
  const showTicketModal = ref(false);
  const selectedTicketId = ref(null);
  const selectedColumnId = ref(null);
  const isUpdatingFromURL = ref(false);

  // Update URL when ticket ID changes
  const updateURL = (ticketId = null) => {
    if (!router || !route) return;
    
    const query = { ...route.query };
    
    if (ticketId) {
      query.ticket = ticketId.toString();
    } else {
      delete query.ticket;
    }
    
    // Only update if different from current URL
    const currentTicketId = route.query.ticket;
    if ((ticketId && currentTicketId !== ticketId.toString()) || (!ticketId && currentTicketId)) {
      isUpdatingFromURL.value = true;
      router.replace({ query });
    }
  };

  const openTicketModal = (ticketId, columnId = null) => {
    // Prevent infinite loop if already opening this ticket
    if (selectedTicketId.value === ticketId && showTicketModal.value) {
      return;
    }
    
    selectedTicketId.value = ticketId;
    selectedColumnId.value = columnId;
    showTicketModal.value = true;
    updateURL(ticketId);
  };

  const closeTicketModal = () => {
    showTicketModal.value = false;
    selectedTicketId.value = null;
    selectedColumnId.value = null;
    updateURL(null);
  };

  // Navigation functions
  const currentColumnTickets = computed(() => {
    if (!selectedColumnId.value || !columnTicketsRef?.value?.[selectedColumnId.value]) {
      return [];
    }
    return columnTicketsRef.value[selectedColumnId.value] || [];
  });

  const currentTicketIndex = computed(() => {
    if (!selectedTicketId.value || !currentColumnTickets.value.length) {
      return -1;
    }
    return currentColumnTickets.value.findIndex(
      ticket => ticket.id === selectedTicketId.value
    );
  });

  const hasNextTicket = computed(() => {
    return (
      currentTicketIndex.value >= 0 &&
      currentTicketIndex.value < currentColumnTickets.value.length - 1
    );
  });

  const hasPreviousTicket = computed(() => {
    return currentTicketIndex.value > 0;
  });

  const navigateToNextTicket = () => {
    if (!hasNextTicket.value) return;
    const nextIndex = currentTicketIndex.value + 1;
    const nextTicket = currentColumnTickets.value[nextIndex];
    if (nextTicket) {
      selectedTicketId.value = nextTicket.id;
      updateURL(nextTicket.id);
    }
  };

  const navigateToPreviousTicket = () => {
    if (!hasPreviousTicket.value) return;
    const prevIndex = currentTicketIndex.value - 1;
    const prevTicket = currentColumnTickets.value[prevIndex];
    if (prevTicket) {
      selectedTicketId.value = prevTicket.id;
      updateURL(prevTicket.id);
    }
  };

  // Function to find column ID for a ticket
  const findColumnIdForTicket = (ticketIdNum) => {
    if (!columnTicketsRef?.value) return null;
    
    for (const [columnId, tickets] of Object.entries(columnTicketsRef.value)) {
      if (tickets.some(t => t.id === ticketIdNum)) {
        return Number(columnId);
      }
    }
    return null;
  };

  // Watch for columnTickets changes to update columnId when tickets are loaded
  if (columnTicketsRef) {
    watch(
      () => columnTicketsRef.value,
      () => {
        // If we have a selected ticket but no columnId, try to find it
        if (selectedTicketId.value && !selectedColumnId.value) {
          const foundColumnId = findColumnIdForTicket(selectedTicketId.value);
          if (foundColumnId) {
            selectedColumnId.value = foundColumnId;
          }
        }
      },
      { deep: true, immediate: true }
    );
  }

  // Watch for ticket being loaded in store to get board_column_id
  if (store) {
    watch(
      () => store.getters['tickets/getCurrentTicket'],
      (currentTicket) => {
        // If we have a selected ticket but no columnId, try to get it from the loaded ticket
        if (
          currentTicket &&
          currentTicket.id === selectedTicketId.value &&
          currentTicket.board_column_id &&
          !selectedColumnId.value
        ) {
          selectedColumnId.value = currentTicket.board_column_id;
        }
      },
      { immediate: true }
    );
  }

  // Watch for URL changes to open modal when ticket query param is present
  if (router && route) {
    watch(
      () => route.query.ticket,
      (ticketId) => {
        // Skip if we're updating from code (not from URL change)
        if (isUpdatingFromURL.value) {
          isUpdatingFromURL.value = false;
          return;
        }
        
        if (ticketId) {
          const ticketIdNum = Number(ticketId);
          if (Number.isFinite(ticketIdNum) && ticketIdNum !== selectedTicketId.value) {
            // Find which column this ticket belongs to
            const ticketColumnId = findColumnIdForTicket(ticketIdNum);
            
            selectedTicketId.value = ticketIdNum;
            selectedColumnId.value = ticketColumnId;
            showTicketModal.value = true;
          }
        } else if (!ticketId && showTicketModal.value) {
          showTicketModal.value = false;
          selectedTicketId.value = null;
          selectedColumnId.value = null;
        }
      },
      { immediate: true }
    );

    // Watch for modal being closed externally (e.g., clicking outside) and update URL
    watch(
      () => showTicketModal.value,
      (isOpen) => {
        if (!isOpen && selectedTicketId.value && !isUpdatingFromURL.value) {
          // Modal was closed externally, update URL and clear state
          selectedTicketId.value = null;
          selectedColumnId.value = null;
          updateURL(null);
        }
      }
    );
  }

  // Create Ticket Modal
  const showCreateTicketModal = ref(false);
  const createTicketColumnId = ref(null);

  const openCreateTicketModal = columnId => {
    createTicketColumnId.value = columnId;
    showCreateTicketModal.value = true;
  };

  const closeCreateTicketModal = () => {
    showCreateTicketModal.value = false;
    createTicketColumnId.value = null;
  };

  const onTicketCreated = async () => {
    closeCreateTicketModal();
    if (onTicketCreatedCallback) {
      await onTicketCreatedCallback();
    }
  };

  return {
    // Ticket View Modal
    showTicketModal,
    selectedTicketId,
    selectedColumnId,
    openTicketModal,
    closeTicketModal,
    // Navigation
    hasNextTicket,
    hasPreviousTicket,
    navigateToNextTicket,
    navigateToPreviousTicket,
    // Create Ticket Modal
    showCreateTicketModal,
    createTicketColumnId,
    openCreateTicketModal,
    closeCreateTicketModal,
    onTicketCreated,
  };
}

